import React, { useEffect, useState } from 'react';
import {
    Box,
    Typography,
    Paper,
    TextField,
    Button,
    Grid,
    Card,
    CardContent,
    Switch,
    FormControlLabel,
    Alert,
    CircularProgress,
    Divider,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Tooltip,
    Avatar,
    Fade,
    Container,
    Tab,
    Tabs,
    useTheme,
    alpha,
    ToggleButton,
    ToggleButtonGroup,
} from '@mui/material';
import {
    Save as SaveIcon,
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Close as CloseIcon,
    Email as EmailIcon,
    Code as CodeIcon,
    Preview as PreviewIcon,
    Security as SecurityIcon,
    Send as SendIcon,
    CheckCircle,
    Email,
    DesktopMac,
    PhoneIphone,
    ContentCopy,
    OpenInNew,
} from '@mui/icons-material';
import { observer } from 'mobx-react-lite';
import { useStore } from '../store';
import api from '../config/api';

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`template-tabpanel-${index}`}
            aria-labelledby={`template-tab-${index}`}
            {...other}
        >
            {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
        </div>
    );
}

const EmailTemplates: React.FC = observer(() => {
    const { emailTemplateStore } = useStore();
    const theme = useTheme();
    const [selectedTemplate, setSelectedTemplate] = useState<number | null>(null);
    const [editedTemplate, setEditedTemplate] = useState({
        subject: '',
        content: '',
        is_active: true,
    });
    const [openCreateDialog, setOpenCreateDialog] = useState(false);
    const [newTemplate, setNewTemplate] = useState({
        name: '',
        subject: '',
        content: '',
        is_active: true,
    });
    const [tabValue, setTabValue] = useState(0);
    const [openTestEmailDialog, setOpenTestEmailDialog] = useState(false);
    const [testEmail, setTestEmail] = useState('');
    const [sendingTestEmail, setSendingTestEmail] = useState(false);
    const [openPreviewDialog, setOpenPreviewDialog] = useState(false);
    const [previewDevice, setPreviewDevice] = useState<'desktop' | 'mobile'>('desktop');

    // Функция для форматирования HTML
    const formatHtml = (html: string): string => {
        // Простое форматирование HTML
        let formatted = html;
        // Добавляем переносы после закрывающих тегов
        formatted = formatted.replace(/>\s*</g, '>\n<');
        // Добавляем отступы
        const lines = formatted.split('\n');
        let indentLevel = 0;
        const formattedLines = lines.map(line => {
            const trimmed = line.trim();
            if (trimmed.startsWith('</')) {
                indentLevel--;
            }
            const indented = '  '.repeat(Math.max(0, indentLevel)) + trimmed;
            if (trimmed.startsWith('<') && !trimmed.startsWith('</') && !trimmed.endsWith('/>') && !trimmed.includes('</')) {
                indentLevel++;
            }
            return indented;
        });
        return formattedLines.join('\n');
    };

    // Функция для обработки шаблона - заменяет переменные на примеры
    const processTemplate = (template: string): string => {
        const sampleData = {
            FullName: 'Иван Иванов',
            Company: 'ООО "Компания"',
            CompanySize: '50',
            Email: 'ivan@company.ru',
            Phone: '+7 (999) 123-45-67',
            Tariff: 'Professional',
            VerificationURL: '#',
            IsRetry: false,
        };

        let processed = template;
        Object.entries(sampleData).forEach(([key, value]) => {
            const regex = new RegExp(`\\{\\{\\.${key}\\}\\}`, 'g');
            processed = processed.replace(regex, String(value));
        });

        return processed;
    };

    // Функция для извлечения переменных из шаблона
    const extractVariables = (template: string): string[] => {
        const regex = /\{\{\.(\w+)\}\}/g;
        const variables = new Set<string>();
        let match;

        while ((match = regex.exec(template)) !== null) {
            variables.add(`{{.${match[1]}}}`);
        }

        return Array.from(variables);
    };

    // Функция для экранирования HTML
    const escapeHtml = (html: string): string => {
        const div = document.createElement('div');
        div.textContent = html;
        return div.innerHTML;
    };

    useEffect(() => {
        emailTemplateStore.fetchTemplates();
    }, [emailTemplateStore]);

    const handleTemplateSelect = (templateId: number) => {
        const template = emailTemplateStore.getTemplateById(templateId);
        if (template) {
            setSelectedTemplate(templateId);
            setEditedTemplate({
                subject: template.subject,
                content: template.content,
                is_active: template.is_active,
            });
            setTabValue(0);
        }
    };

    const handleSave = async () => {
        if (selectedTemplate) {
            const success = await emailTemplateStore.updateTemplate(selectedTemplate, editedTemplate);
            if (success) {
                // Template saved successfully
            }
        }
    };

    const handleCreate = async () => {
        const success = await emailTemplateStore.createTemplate(newTemplate);
        if (success) {
            setOpenCreateDialog(false);
            setNewTemplate({
                name: '',
                subject: '',
                content: '',
                is_active: true,
            });
        }
    };

    const handleDelete = async (id: number) => {
        if (window.confirm('Вы уверены, что хотите удалить этот шаблон?')) {
            const success = await emailTemplateStore.deleteTemplate(id);
            if (success && selectedTemplate === id) {
                setSelectedTemplate(null);
            }
        }
    };

    const handleSendTestEmail = async () => {
        if (selectedTemplate && testEmail) {
            setSendingTestEmail(true);
            try {
                const response = await api.post('/api/email-templates/test', {
                    template_id: selectedTemplate,
                    to_email: testEmail,
                });

                if (response.data.success) {
                    // Показываем успешное уведомление
                    alert(`Тестовое письмо успешно отправлено на ${testEmail}`);
                    setOpenTestEmailDialog(false);
                    setTestEmail('');
                }
            } catch (error: any) {
                alert(error.response?.data?.error || 'Ошибка при отправке тестового письма');
            } finally {
                setSendingTestEmail(false);
            }
        }
    };

    const handlePreviewDeviceChange = (
        event: React.MouseEvent<HTMLElement>,
        newDevice: 'desktop' | 'mobile' | null,
    ) => {
        if (newDevice !== null) {
            setPreviewDevice(newDevice);
        }
    };

    const getTemplateDescription = (name: string) => {
        const descriptions = {
            'email_verification': 'Отправляется для подтверждения email адреса',
            'application_processing': 'Отправляется когда заявка принята в обработку',
            'application_reminder': 'Напоминание о необходимости подтвердить email',
            'product_information': 'Информация о продукте для клиента',
        };
        return descriptions[name as keyof typeof descriptions] || 'Пользовательский шаблон';
    };

    const getAvailableVariables = (name: string) => {
        const variables = {
            'email_verification': ['{{.FullName}}', '{{.VerificationURL}}', '{{.IsRetry}}'],
            'application_processing': ['{{.FullName}}', '{{.Company}}', '{{.CompanySize}}', '{{.Tariff}}', '{{.Phone}}'],
            'application_reminder': ['{{.FullName}}', '{{.VerificationURL}}'],
            'product_information': ['{{.FullName}}', '{{.Company}}', '{{.CompanySize}}', '{{.Tariff}}', '{{.Phone}}'],
        };
        return variables[name as keyof typeof variables] || ['{{.FullName}}', '{{.Company}}', '{{.Email}}', '{{.Phone}}'];
    };

    const isSystemTemplate = (name: string) => {
        return ['email_verification', 'application_processing', 'application_reminder', 'product_information'].includes(name);
    };

    const TemplateCard = ({ template }: { template: any }) => (
        <Fade in timeout={300}>
            <Card
                sx={{
                    mb: 2,
                    cursor: 'pointer',
                    border: selectedTemplate === template.id ? 2 : 1,
                    borderColor: selectedTemplate === template.id ? 'primary.main' : 'divider',
                    transition: 'all 0.3s ease-in-out',
                    '&:hover': {
                        transform: selectedTemplate === template.id ? 'none' : 'translateY(-2px)',
                        boxShadow: selectedTemplate === template.id ? 4 : 2,
                    }
                }}
                onClick={() => handleTemplateSelect(template.id)}
            >
                <CardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                        <Box display="flex" alignItems="center">
                            <Avatar
                                sx={{
                                    bgcolor: template.is_active ? 'primary.main' : 'grey.400',
                                    mr: 2,
                                    width: 40,
                                    height: 40,
                                }}
                            >
                                <EmailIcon />
                            </Avatar>
                            <Box>
                                <Typography variant="h6" fontWeight="bold">
                                    {template.name}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {getTemplateDescription(template.name)}
                                </Typography>
                            </Box>
                        </Box>
                        <Box display="flex" alignItems="center" gap={1}>
                            <Chip
                                label={template.is_active ? 'Активен' : 'Неактивен'}
                                color={template.is_active ? 'success' : 'default'}
                                size="small"
                            />
                            {isSystemTemplate(template.name) && (
                                <Chip
                                    label="Системный"
                                    color="warning"
                                    size="small"
                                    icon={<SecurityIcon />}
                                />
                            )}
                            {!isSystemTemplate(template.name) && (
                                <Tooltip title="Удалить шаблон">
                                    <IconButton
                                        size="small"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleDelete(template.id);
                                        }}
                                    >
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            )}
                        </Box>
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                        Изменен: {new Date(template.updated_at).toLocaleDateString('ru-RU')}
                    </Typography>
                </CardContent>
            </Card>
        </Fade>
    );

    if (emailTemplateStore.loading) {
        return (
            <Container maxWidth="xl">
                <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="400px">
                    <CircularProgress size={60} sx={{ mb: 2 }} />
                    <Typography variant="h6" color="textSecondary">
                        Загрузка шаблонов...
                    </Typography>
                </Box>
            </Container>
        );
    }

    const selectedTemplateData = selectedTemplate ? emailTemplateStore.getTemplateById(selectedTemplate) : null;

    return (
        <Container maxWidth="xl">
            <Box mb={4}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                    <Box>
                        <Typography variant="h3" gutterBottom fontWeight="bold" color="primary">
                            Email шаблоны
                        </Typography>
                        <Typography variant="h6" color="textSecondary">
                            Управление шаблонами электронных писем
                        </Typography>
                    </Box>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => setOpenCreateDialog(true)}
                        size="large"
                    >
                        Создать шаблон
                    </Button>
                </Box>

                {emailTemplateStore.error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {emailTemplateStore.error}
                    </Alert>
                )}

                <Grid container spacing={3}>
                    {/* Список шаблонов */}
                    <Grid item xs={12} md={4}>
                        <Typography variant="h6" gutterBottom fontWeight="bold">
                            Доступные шаблоны ({emailTemplateStore.templates.length})
                        </Typography>

                        {/* Статистика */}
                        <Grid container spacing={1} sx={{ mb: 2 }}>
                            <Grid item xs={6}>
                                <Paper sx={{ p: 1, textAlign: 'center' }}>
                                    <Typography variant="h6" color="success.main">
                                        {emailTemplateStore.activeTemplates.length}
                                    </Typography>
                                    <Typography variant="caption">Активных</Typography>
                                </Paper>
                            </Grid>
                            <Grid item xs={6}>
                                <Paper sx={{ p: 1, textAlign: 'center' }}>
                                    <Typography variant="h6" color="warning.main">
                                        {emailTemplateStore.templates.filter(t => isSystemTemplate(t.name)).length}
                                    </Typography>
                                    <Typography variant="caption">Системных</Typography>
                                </Paper>
                            </Grid>
                        </Grid>

                        {emailTemplateStore.templates.map((template) => (
                            <TemplateCard key={template.id} template={template} />
                        ))}

                        {emailTemplateStore.templates.length === 0 && (
                            <Paper sx={{ p: 4, textAlign: 'center' }}>
                                <EmailIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                                <Typography variant="h6" color="textSecondary" gutterBottom>
                                    Нет шаблонов
                                </Typography>
                                <Typography variant="body2" color="textSecondary" paragraph>
                                    Создайте первый email шаблон
                                </Typography>
                                <Button
                                    variant="contained"
                                    startIcon={<AddIcon />}
                                    onClick={() => setOpenCreateDialog(true)}
                                >
                                    Создать шаблон
                                </Button>
                            </Paper>
                        )}
                    </Grid>

                    {/* Редактор шаблона */}
                    <Grid item xs={12} md={8}>
                        {selectedTemplate && selectedTemplateData ? (
                            <Paper sx={{ height: 'fit-content' }}>
                                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                                    <Tabs
                                        value={tabValue}
                                        onChange={(_, newValue) => setTabValue(newValue)}
                                        variant="fullWidth"
                                    >
                                        <Tab icon={<EditIcon />} label="Редактирование" />
                                        <Tab
                                            icon={<PreviewIcon />}
                                            label="Предварительный просмотр"
                                            onClick={() => {
                                                if (tabValue === 1) {
                                                    setOpenPreviewDialog(true);
                                                }
                                            }}
                                        />
                                    </Tabs>
                                </Box>

                                <TabPanel value={tabValue} index={0}>
                                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                                        <Typography variant="h6" fontWeight="bold">
                                            Редактирование: {selectedTemplateData.name}
                                        </Typography>
                                        <Box display="flex" gap={1}>
                                            {isSystemTemplate(selectedTemplateData.name) && (
                                                <Chip
                                                    label="Системный шаблон"
                                                    color="warning"
                                                    size="small"
                                                    icon={<SecurityIcon />}
                                                />
                                            )}
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                startIcon={<Email />}
                                                onClick={() => setOpenTestEmailDialog(true)}
                                            >
                                                Тестовая отправка
                                            </Button>
                                        </Box>
                                    </Box>

                                    {/* Доступные переменные */}
                                    {(() => {
                                        const variables = getAvailableVariables(selectedTemplateData.name);

                                        if (variables.length > 0) {
                                            return (
                                                <Alert severity="info" sx={{ mb: 3 }}>
                                                    <Typography variant="subtitle2" gutterBottom>
                                                        Доступные переменные:
                                                    </Typography>
                                                    <Box display="flex" gap={1} flexWrap="wrap">
                                                        {variables.map((variable) => (
                                                            <Chip
                                                                key={variable}
                                                                label={variable}
                                                                size="small"
                                                                variant="outlined"
                                                                onClick={() => {
                                                                    // Вставляем переменную в позицию курсора
                                                                    const textarea = document.querySelector('textarea[name="content"]') as HTMLTextAreaElement;
                                                                    if (textarea) {
                                                                        const start = textarea.selectionStart;
                                                                        const end = textarea.selectionEnd;
                                                                        const text = editedTemplate.content;
                                                                        const before = text.substring(0, start);
                                                                        const after = text.substring(end);

                                                                        setEditedTemplate(prev => ({
                                                                            ...prev,
                                                                            content: before + variable + after
                                                                        }));

                                                                        // Восстанавливаем фокус и позицию курсора
                                                                        setTimeout(() => {
                                                                            textarea.focus();
                                                                            textarea.setSelectionRange(start + variable.length, start + variable.length);
                                                                        }, 0);
                                                                    }
                                                                }}
                                                                sx={{ cursor: 'pointer' }}
                                                            />
                                                        ))}
                                                    </Box>
                                                    <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                                                        Кликните на переменную, чтобы вставить в позицию курсора
                                                    </Typography>
                                                </Alert>
                                            );
                                        }
                                        return null;
                                    })()}

                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={editedTemplate.is_active}
                                                onChange={(e) => setEditedTemplate(prev => ({
                                                    ...prev,
                                                    is_active: e.target.checked,
                                                }))}
                                            />
                                        }
                                        label="Активный шаблон"
                                        sx={{ mb: 2 }}
                                    />

                                    <TextField
                                        fullWidth
                                        label="Тема письма"
                                        value={editedTemplate.subject}
                                        onChange={(e) => setEditedTemplate(prev => ({
                                            ...prev,
                                            subject: e.target.value,
                                        }))}
                                        margin="normal"
                                        placeholder="Введите тему письма"
                                        helperText={`Предпросмотр: ${processTemplate(editedTemplate.subject)}`}
                                    />

                                    <Box sx={{ mt: 2, position: 'relative' }}>
                                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                            <Typography variant="body2" color="text.secondary">
                                                Содержимое письма (HTML)
                                            </Typography>
                                            <Box display="flex" gap={1}>
                                                <Button
                                                    size="small"
                                                    startIcon={<CodeIcon />}
                                                    onClick={() => {
                                                        // Форматирование HTML
                                                        try {
                                                            const formatted = formatHtml(editedTemplate.content);
                                                            setEditedTemplate(prev => ({
                                                                ...prev,
                                                                content: formatted
                                                            }));
                                                        } catch (e) {
                                                            console.error('Failed to format HTML:', e);
                                                        }
                                                    }}
                                                >
                                                    Форматировать
                                                </Button>
                                                <Button
                                                    size="small"
                                                    startIcon={<PreviewIcon />}
                                                    onClick={() => setOpenPreviewDialog(true)}
                                                >
                                                    Предпросмотр
                                                </Button>
                                            </Box>
                                        </Box>
                                        <TextField
                                            fullWidth
                                            name="content"
                                            value={editedTemplate.content}
                                            onChange={(e) => setEditedTemplate(prev => ({
                                                ...prev,
                                                content: e.target.value,
                                            }))}
                                            multiline
                                            rows={15}
                                            placeholder="Введите HTML содержимое письма"
                                            sx={{
                                                '& .MuiInputBase-input': {
                                                    fontFamily: 'Monaco, Menlo, "Ubuntu Mono", monospace',
                                                    fontSize: '14px',
                                                    lineHeight: '1.5',
                                                },
                                            }}
                                        />
                                    </Box>

                                    <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                                        <Button
                                            variant="contained"
                                            startIcon={<SaveIcon />}
                                            onClick={handleSave}
                                            disabled={emailTemplateStore.saving}
                                            size="large"
                                        >
                                            {emailTemplateStore.saving ? 'Сохранение...' : 'Сохранить изменения'}
                                        </Button>
                                        <Button
                                            variant="outlined"
                                            onClick={() => {
                                                setEditedTemplate({
                                                    subject: selectedTemplateData.subject,
                                                    content: selectedTemplateData.content,
                                                    is_active: selectedTemplateData.is_active,
                                                });
                                            }}
                                        >
                                            Отменить изменения
                                        </Button>
                                    </Box>
                                </TabPanel>

                                <TabPanel value={tabValue} index={1}>
                                    <Box textAlign="center" py={8}>
                                        <Typography variant="h6" gutterBottom>
                                            Предварительный просмотр
                                        </Typography>
                                        <Typography variant="body1" color="text.secondary" paragraph>
                                            Нажмите кнопку ниже для открытия предпросмотра
                                        </Typography>
                                        <Button
                                            variant="contained"
                                            size="large"
                                            startIcon={<PreviewIcon />}
                                            onClick={() => setOpenPreviewDialog(true)}
                                        >
                                            Открыть предпросмотр
                                        </Button>
                                    </Box>
                                </TabPanel>
                            </Paper>
                        ) : (
                            <Paper sx={{ p: 6, textAlign: 'center', height: 'fit-content' }}>
                                <EmailIcon sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
                                <Typography variant="h5" color="textSecondary" gutterBottom>
                                    Выберите шаблон для редактирования
                                </Typography>
                                <Typography variant="body1" color="textSecondary" paragraph>
                                    Кликните на один из шаблонов слева, чтобы начать редактирование
                                </Typography>
                                <Button
                                    variant="outlined"
                                    startIcon={<AddIcon />}
                                    onClick={() => setOpenCreateDialog(true)}
                                >
                                    Создать новый шаблон
                                </Button>
                            </Paper>
                        )}
                    </Grid>
                </Grid>

                {/* Диалог создания нового шаблона */}
                <Dialog open={openCreateDialog} onClose={() => setOpenCreateDialog(false)} maxWidth="md" fullWidth>
                    <DialogTitle>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Box display="flex" alignItems="center">
                                <AddIcon sx={{ mr: 1 }} />
                                Создать новый шаблон
                            </Box>
                            <IconButton onClick={() => setOpenCreateDialog(false)}>
                                <CloseIcon />
                            </IconButton>
                        </Box>
                    </DialogTitle>
                    <DialogContent>
                        <Grid container spacing={2} sx={{ mt: 1 }}>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Название шаблона"
                                    value={newTemplate.name}
                                    onChange={(e) => setNewTemplate(prev => ({
                                        ...prev,
                                        name: e.target.value,
                                    }))}
                                    placeholder="unique_template_name"
                                    helperText="Уникальное имя шаблона (только латинские буквы, цифры и подчеркивания)"
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Тема письма"
                                    value={newTemplate.subject}
                                    onChange={(e) => setNewTemplate(prev => ({
                                        ...prev,
                                        subject: e.target.value,
                                    }))}
                                    placeholder="Введите тему письма"
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Содержимое письма (HTML)"
                                    value={newTemplate.content}
                                    onChange={(e) => setNewTemplate(prev => ({
                                        ...prev,
                                        content: e.target.value,
                                    }))}
                                    multiline
                                    rows={10}
                                    placeholder="Введите HTML содержимое письма"
                                    sx={{
                                        '& .MuiInputBase-input': {
                                            fontFamily: 'Monaco, Menlo, "Ubuntu Mono", monospace',
                                            fontSize: '14px',
                                        },
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={newTemplate.is_active}
                                            onChange={(e) => setNewTemplate(prev => ({
                                                ...prev,
                                                is_active: e.target.checked,
                                            }))}
                                        />
                                    }
                                    label="Активный шаблон"
                                />
                            </Grid>
                        </Grid>
                    </DialogContent>
                    <DialogActions sx={{ p: 3 }}>
                        <Button
                            onClick={() => setOpenCreateDialog(false)}
                            disabled={emailTemplateStore.saving}
                        >
                            Отмена
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleCreate}
                            disabled={emailTemplateStore.saving || !newTemplate.name || !newTemplate.subject}
                            startIcon={<AddIcon />}
                        >
                            {emailTemplateStore.saving ? 'Создание...' : 'Создать шаблон'}
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Диалог тестовой отправки email */}
                <Dialog open={openTestEmailDialog} onClose={() => setOpenTestEmailDialog(false)} maxWidth="sm" fullWidth>
                    <DialogTitle>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Box display="flex" alignItems="center">
                                <EmailIcon sx={{ mr: 1 }} />
                                Тестовая отправка email
                            </Box>
                            <IconButton onClick={() => setOpenTestEmailDialog(false)}>
                                <CloseIcon />
                            </IconButton>
                        </Box>
                    </DialogTitle>
                    <DialogContent>
                        <Alert severity="info" sx={{ mb: 2 }}>
                            Отправьте тестовое письмо для проверки шаблона
                        </Alert>
                        <TextField
                            fullWidth
                            label="Email получателя"
                            type="email"
                            value={testEmail}
                            onChange={(e) => setTestEmail(e.target.value)}
                            placeholder="test@example.com"
                            margin="normal"
                            helperText="Введите email адрес для тестовой отправки"
                        />
                    </DialogContent>
                    <DialogActions sx={{ p: 3 }}>
                        <Button onClick={() => setOpenTestEmailDialog(false)}>
                            Отмена
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleSendTestEmail}
                            disabled={!testEmail || !testEmail.includes('@') || sendingTestEmail}
                            startIcon={sendingTestEmail ? <CircularProgress size={20} /> : <SendIcon />}
                        >
                            {sendingTestEmail ? 'Отправка...' : 'Отправить тест'}
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Диалог предпросмотра email */}
                <Dialog
                    open={openPreviewDialog}
                    onClose={() => setOpenPreviewDialog(false)}
                    maxWidth={previewDevice === 'desktop' ? 'lg' : 'sm'}
                    fullWidth
                    PaperProps={{
                        sx: { height: '90vh', display: 'flex', flexDirection: 'column' }
                    }}
                >
                    <DialogTitle sx={{ pb: 1 }}>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="h6">Предварительный просмотр email</Typography>
                            <Box display="flex" alignItems="center" gap={2}>
                                <ToggleButtonGroup
                                    value={previewDevice}
                                    exclusive
                                    onChange={handlePreviewDeviceChange}
                                    size="small"
                                >
                                    <ToggleButton value="desktop">
                                        <DesktopMac sx={{ mr: 1 }} />
                                        Десктоп
                                    </ToggleButton>
                                    <ToggleButton value="mobile">
                                        <PhoneIphone sx={{ mr: 1 }} />
                                        Мобильный
                                    </ToggleButton>
                                </ToggleButtonGroup>
                                <IconButton onClick={() => setOpenPreviewDialog(false)}>
                                    <CloseIcon />
                                </IconButton>
                            </Box>
                        </Box>
                    </DialogTitle>
                    <Divider />
                    <DialogContent sx={{ flex: 1, p: 0, bgcolor: '#f5f5f5', overflow: 'auto' }}>
                        <Box
                            sx={{
                                minHeight: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                p: previewDevice === 'mobile' ? 2 : 4,
                            }}
                        >
                            {/* Email клиент UI */}
                            <Paper
                                elevation={3}
                                sx={{
                                    width: previewDevice === 'desktop' ? '100%' : '375px',
                                    maxWidth: previewDevice === 'desktop' ? '800px' : '375px',
                                    mx: 'auto',
                                    borderRadius: 2,
                                    overflow: 'hidden',
                                    border: '1px solid',
                                    borderColor: 'divider',
                                }}
                            >
                                {/* Email заголовок */}
                                <Box sx={{
                                    bgcolor: 'grey.50',
                                    p: 2,
                                    borderBottom: '1px solid',
                                    borderColor: 'divider',
                                }}>
                                    <Grid container spacing={2} alignItems="center">
                                        <Grid item xs={12}>
                                            <Box display="flex" alignItems="center" gap={1} mb={1}>
                                                <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
                                                    A
                                                </Avatar>
                                                <Box flex={1}>
                                                    <Typography variant="body2" fontWeight={600}>
                                                        Applications Management System
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        noreply@yourcompany.com
                                                    </Typography>
                                                </Box>
                                                <Typography variant="caption" color="text.secondary">
                                                    {new Date().toLocaleString('ru-RU')}
                                                </Typography>
                                            </Box>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Typography variant="body2" color="text.secondary">
                                                <strong>Кому:</strong> user@example.com
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Typography variant="h6" fontWeight={500}>
                                                {processTemplate(editedTemplate.subject) || 'Без темы'}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </Box>

                                {/* Email контент */}
                                <Box sx={{
                                    bgcolor: '#ffffff',
                                    minHeight: 400,
                                    p: 0,
                                }}>
                                    <Box
                                        sx={{
                                            p: 0,
                                            width: '100%',
                                            '& img': {
                                                maxWidth: '100%',
                                                height: 'auto',
                                            },
                                            '& a': {
                                                color: theme.palette.primary.main,
                                                textDecoration: 'none',
                                                '&:hover': {
                                                    textDecoration: 'underline',
                                                }
                                            },
                                            '& table': {
                                                width: '100%',
                                                borderCollapse: 'collapse',
                                            },
                                            '& .button': {
                                                display: 'inline-block',
                                                padding: '12px 24px',
                                                backgroundColor: theme.palette.primary.main,
                                                color: 'white',
                                                borderRadius: '4px',
                                                textDecoration: 'none',
                                                fontWeight: 'bold',
                                                '&:hover': {
                                                    backgroundColor: theme.palette.primary.dark,
                                                }
                                            },
                                            // Масштабирование для мобильной версии
                                            ...(previewDevice === 'mobile' && {
                                                fontSize: '14px',
                                                '& h1': { fontSize: '22px' },
                                                '& h2': { fontSize: '18px' },
                                                '& h3': { fontSize: '16px' },
                                                '& p': { fontSize: '14px' },
                                                '& .button': {
                                                    padding: '10px 20px',
                                                    fontSize: '14px',
                                                },
                                            })
                                        }}
                                        dangerouslySetInnerHTML={{
                                            __html: processTemplate(editedTemplate.content)
                                        }}
                                    />
                                </Box>

                                {/* Email футер с действиями */}
                                <Box sx={{
                                    bgcolor: 'grey.50',
                                    p: 2,
                                    borderTop: '1px solid',
                                    borderColor: 'divider',
                                    display: 'flex',
                                    flexDirection: previewDevice === 'mobile' ? 'column' : 'row',
                                    justifyContent: 'space-between',
                                    alignItems: previewDevice === 'mobile' ? 'stretch' : 'center',
                                    gap: 2,
                                }}>
                                    <Box display="flex" gap={1} flexWrap="wrap">
                                        <Chip
                                            size="small"
                                            icon={<CheckCircle />}
                                            label="HTML письмо"
                                            color="success"
                                            variant="outlined"
                                        />
                                        {selectedTemplateData && (
                                            <Chip
                                                size="small"
                                                icon={<EmailIcon />}
                                                label={`Шаблон: ${selectedTemplateData.name}`}
                                                variant="outlined"
                                            />
                                        )}
                                    </Box>
                                    <Box display="flex" gap={1} justifyContent={previewDevice === 'mobile' ? 'center' : 'flex-end'}>
                                        <Tooltip title="Скопировать HTML">
                                            <IconButton
                                                size="small"
                                                onClick={() => {
                                                    navigator.clipboard.writeText(editedTemplate.content);
                                                    alert('HTML скопирован в буфер обмена');
                                                }}
                                            >
                                                <ContentCopy fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Открыть в новом окне">
                                            <IconButton
                                                size="small"
                                                onClick={() => {
                                                    const newWindow = window.open('', '_blank');
                                                    if (newWindow) {
                                                        newWindow.document.write(processTemplate(editedTemplate.content));
                                                        newWindow.document.close();
                                                    }
                                                }}
                                            >
                                                <OpenInNew fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                </Box>
                            </Paper>
                        </Box>
                    </DialogContent>
                    <Divider />
                    <DialogActions sx={{ p: 2 }}>
                        <Box width="100%">
                            <Alert severity="info" sx={{ mb: 2 }}>
                                <Typography variant="body2">
                                    <strong>Используемые переменные:</strong>{' '}
                                    {extractVariables(editedTemplate.content).join(', ') || 'Нет переменных'}
                                </Typography>
                            </Alert>
                            <Box display="flex" justifyContent="space-between">
                                <Button
                                    variant="outlined"
                                    onClick={() => {
                                        const newWindow = window.open('', '_blank');
                                        if (newWindow) {
                                            newWindow.document.write(`
                                                <html>
                                                    <head>
                                                        <title>HTML Source</title>
                                                        <style>
                                                            body { 
                                                                font-family: monospace; 
                                                                padding: 20px;
                                                                background: #f5f5f5;
                                                            }
                                                            pre {
                                                                background: white;
                                                                padding: 20px;
                                                                border-radius: 8px;
                                                                overflow: auto;
                                                            }
                                                        </style>
                                                    </head>
                                                    <body>
                                                        <h2>HTML код письма</h2>
                                                        <pre>${escapeHtml(editedTemplate.content)}</pre>
                                                    </body>
                                                </html>
                                            `);
                                            newWindow.document.close();
                                        }
                                    }}
                                    startIcon={<CodeIcon />}
                                >
                                    Показать HTML
                                </Button>
                                <Button onClick={() => setOpenPreviewDialog(false)}>
                                    Закрыть
                                </Button>
                            </Box>
                        </Box>
                    </DialogActions>
                </Dialog>

                {/* Справочная информация */}
                <Paper sx={{ p: 3, mt: 4, bgcolor: alpha(theme.palette.info.main, 0.1) }}>
                    <Typography variant="h6" gutterBottom fontWeight="bold">
                        💡 Справка по шаблонам
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <Typography variant="body2" paragraph>
                                <strong>Переменные:</strong> Используйте переменные в формате <code>{'{{.VariableName}}'}</code> для вставки динамических данных.
                            </Typography>
                            <Typography variant="body2" paragraph>
                                <strong>HTML:</strong> Шаблоны поддерживают полную HTML разметку для красивого оформления писем.
                            </Typography>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Typography variant="body2" paragraph>
                                <strong>Активность:</strong> Неактивные шаблоны не будут использоваться для отправки писем.
                            </Typography>
                            <Typography variant="body2">
                                <strong>Системные шаблоны:</strong> Шаблоны защищенные от удаления используются системой автоматически.
                            </Typography>
                        </Grid>
                    </Grid>
                </Paper>
            </Box>
        </Container>
    );
});

export default EmailTemplates;